// DEPLOY: /var/www/woody.wtf/frontend/src/components/AuditPanel.js
// Shared AI-audit popups, used by the AI Audit page (AdminAudit.js) and the
// Company page's admin Audit button:
//   AuditPanel  - one announcement row's audits so far, plus the OpenRouter model
//                 picker to start new ones (/admin/audit/run)
//   AuditResult - one audit laid out from its JSON
//   ModelPicker, startBtn, badge, auditBadges, resultRow, items, short, price -
//                 also used by the AI Audit page's overall section
// A row passed in needs source, id, instrument_id, ticker, headline, summary,
// significance, direction and audits (as /admin/audit/rows or /admin/audit/for
// give them). Moved here unchanged from AdminAudit.js so both pages share one copy.
// A suggested prompt change names its prompt (prompt_target; older audits: the base
// summary) and opens the AI Prompts page on it with the suggestion pinned.
// Session audits (audit_session.py) list every announcement of the trading session with
// its part in the price move (result.group).
import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';

const s = {
  select:  {
    padding: '6px 8px', fontSize: '13px', fontFamily: "'DM Sans', sans-serif", border: '1px solid var(--border)',
    borderRadius: 'var(--radius)', background: 'var(--bg)', color: 'var(--text)', outline: 'none',
  },
  btn:     { padding: '4px 10px', borderRadius: 'var(--radius)', border: 'none', cursor: 'pointer', fontSize: '12px', fontWeight: 500, fontFamily: "'DM Sans', sans-serif" },
  num:     { fontFamily: "'DM Mono', monospace", textAlign: 'right', whiteSpace: 'nowrap' },
  link:    { color: 'var(--accent)', cursor: 'pointer' },
  flag:    { display: 'inline-block', marginTop: '4px', padding: '1px 6px', borderRadius: '4px', fontSize: '10px', background: '#FEF3C7', color: '#92400E', whiteSpace: 'nowrap' },
  label:   { fontSize: '11px', color: 'var(--text-muted)', marginBottom: '4px' },
  err:     { color: 'var(--error)', fontSize: '13px', marginTop: '8px' },
  muted:   { color: 'var(--text-muted)' },
  modal:   { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' },
  box:     { background: 'var(--surface)', borderRadius: 'calc(var(--radius) * 2)', padding: '20px', maxWidth: '800px', width: '100%', maxHeight: '85vh', overflow: 'auto' },
  pre:     { whiteSpace: 'pre-wrap', fontFamily: "'DM Mono', monospace", fontSize: '12px', lineHeight: 1.5, margin: 0 },
};

export const MAX_MODELS = 5;                       // as admin.py _AUDIT_MAX_MODELS
export const short = iso => new Date(iso).toLocaleString('en-GB', {
  day: '2-digit', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit',
});
export const price = v => v == null ? '—' : v < 1 ? v.toFixed(4) : v.toFixed(2);
const usd = v => v ? `$${v < 1 ? v.toFixed(2) : v.toFixed(v < 10 ? 2 : 0)}` : 'free';

export const badge = (text, title) => (
  <span title={title} style={{ ...s.flag, marginTop: 0, marginLeft: '6px', background: 'var(--border)', color: 'var(--text-muted)' }}>{text}</span>
);
// Badges on an audit: judged an older prompt, or the row has been re-summarised since.
export const auditBadges = (a, live) => (
  <>
    {a.summary_prompt && live && a.summary_prompt !== live && badge('old prompt', 'Judged a summary made by an earlier prompt version')}
    {a.changed && badge('summary changed since', 'The row has been re-summarised since this audit - it judged the earlier summary')}
  </>
);

// Searchable OpenRouter model list with checkboxes, up to MAX_MODELS.
export function ModelPicker({ models, pick, setPick }) {
  const [q, setQ] = useState('');
  const list = useMemo(() => {
    const t = q.trim().toLowerCase();
    return (models || []).filter(m => !t || m.id.toLowerCase().includes(t) || m.name.toLowerCase().includes(t));
  }, [models, q]);
  const toggle = id => setPick(p => p.includes(id) ? p.filter(x => x !== id)
                                   : p.length < MAX_MODELS ? [...p, id] : p);
  if (!models) return <div style={s.muted}>Loading models…</div>;
  return (
    <>
      <input style={{ ...s.select, width: '100%', boxSizing: 'border-box', marginBottom: '6px' }}
             placeholder="Search, e.g. claude, gpt, gemini" value={q} onChange={e => setQ(e.target.value)} />
      <div style={{ maxHeight: '300px', overflow: 'auto', border: '1px solid var(--border)', borderRadius: 'var(--radius)' }}>
        {list.map(m => (
          <label key={m.id} style={{ display: 'flex', gap: '8px', alignItems: 'center', padding: '4px 8px', fontSize: '12px', cursor: 'pointer',
                                     background: pick.includes(m.id) ? 'var(--accent-dim)' : 'transparent' }}>
            <input type="checkbox" checked={pick.includes(m.id)} onChange={() => toggle(m.id)} />
            <span style={{ flex: 1 }}>{m.name}<span style={{ ...s.muted, marginLeft: '6px' }}>{m.id}</span></span>
            {!m.reasoning && <span style={{ ...s.muted, fontSize: '10px' }} title="No reasoning setting - runs without extended thinking">no reasoning</span>}
            <span style={{ ...s.num, ...s.muted }}>{usd(m.prompt)} / {usd(m.completion)}</span>
          </label>
        ))}
      </div>
    </>
  );
}

export const startBtn = (n, busy, onClick, what) => (
  <button onClick={onClick} disabled={!n || busy}
          style={{ ...s.btn, padding: '6px 14px', background: n ? 'var(--accent)' : 'var(--border)',
                   color: n ? '#fff' : 'var(--text-muted)', cursor: n ? 'pointer' : 'default' }}>
    {busy ? 'Starting…' : `${what} with ${n || ''} model${n === 1 ? '' : 's'}`}
  </button>
);

// Audit popup for one row: pick OpenRouter models and start, and the row's
// audits so far (click one to read it).
export function AuditPanel({ r, models, live, onStarted, onOpen, onClose }) {
  const [pick, setPick] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr]   = useState('');

  const start = async () => {
    setBusy(true); setErr('');
    try {
      const res = await api.post('/admin/audit/run', { source: r.source, id: r.id, instrument_id: r.instrument_id, models: pick });
      onStarted(r, res.data.audit_ids, pick);
      setPick([]);
    } catch (e) {
      setErr(e.response?.data?.error || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={s.modal} onClick={onClose}>
      <div style={s.box} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
          <strong style={{ fontSize: '14px' }}>Audit · {r.ticker} · {r.headline}</strong>
          <span style={s.link} onClick={onClose}>Close ✕</span>
        </div>

        {r.audits.length > 0 && (
          <div style={{ marginBottom: '16px' }}>
            <div style={s.label}>Audits of this announcement</div>
            {r.audits.map(a => (
              <div key={a.audit_id} style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', fontSize: '12px', padding: '3px 0' }}>
                <span>
                  {short(a.created_at)} · {a.model}{a.verdict && ` · ${a.verdict}`}{auditBadges(a, live)}
                </span>
                {a.status === 'running' ? <span style={s.muted}>running…</span>
                  : <span style={{ ...s.link, color: a.status === 'error' ? 'var(--error)' : 'var(--accent)' }}
                          onClick={() => onOpen(a.audit_id)}>{a.status === 'error' ? 'failed - view' : 'view'}</span>}
              </div>
            ))}
          </div>
        )}

        <div style={s.label}>
          Models - pick up to {MAX_MODELS}. Each runs on high reasoning effort with the full document,
          the summary and prompt, the company history and the price action. Prices are US$ per million tokens (in / out).
        </div>
        <ModelPicker models={models} pick={pick} setPick={setPick} />
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '10px' }}>
          {startBtn(pick.length, busy, start, 'Audit')}
          <span style={{ ...s.muted, fontSize: '12px' }}>{pick.join(', ')}</span>
        </div>
        {err && <div style={s.err}>✕ {err}</div>}
      </div>
    </div>
  );
}

export const resultRow = (label, value) => value != null && value !== '' && (
  <div style={{ marginBottom: '10px' }}>
    <div style={s.label}>{label}</div>
    <div style={{ fontSize: '13px' }}>{value}</div>
  </div>
);
export const items = xs => xs?.length ? <ul style={{ margin: 0, paddingLeft: '18px' }}>{xs.map((x, i) => <li key={i}>{x}</li>)}</ul> : 'none';

// One audit's result, laid out from its JSON. "Was" is what the audit judged
// (its own copy), not the row's current rating.
export function AuditResult({ a, r, live, onClose }) {
  const navigate = useNavigate();
  const res = a.result;
  const was = a.audited_significance != null ? `${a.audited_significance}/5 ${a.audited_direction}` : `${r?.significance}/5 ${r?.direction}`;
  return (
    <div style={s.modal} onClick={onClose}>
      <div style={s.box} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
          <strong style={{ fontSize: '14px' }}>Audit · {r?.ticker} · {a.model}</strong>
          <span style={s.link} onClick={onClose}>Close ✕</span>
        </div>
        <div style={{ ...s.muted, fontSize: '11px', marginBottom: '14px' }}>
          {short(a.created_at)} · entry {price(a.entry_price)}{a.entry_manual ? ' (manual)' : ''}
          {a.tokens_in != null && ` · ${a.tokens_in.toLocaleString()} in / ${a.tokens_out.toLocaleString()} out tokens`}
          {a.summary_prompt && ` · prompt ${short(a.summary_prompt)}`}
          {auditBadges({ ...a, changed: a.audited_summary != null && r && a.audited_summary !== r.summary }, live)}
        </div>
        {a.status === 'error' && <div style={s.err}>✕ {a.error}</div>}
        {res && (
          <>
            {res.code_score && resultRow('Code score', <strong>
              {res.code_score.direction_mark} · expected move {res.code_score.band_mark} · measured {res.code_score.total > 0 ? '+' : ''}{res.code_score.total}% (band {res.code_score.band}%){res.code_score.ticks != null && `, ${res.code_score.ticks} ticks`}{res.code_score.role ? ` · ${res.code_score.role}` : ''}
            </strong>)}
            {resultRow(res.code_score ? 'Auditor' : 'Verdict', <>
              <strong>{res.code_score ? '' : `${a.verdict || res.rating_verdict} - `}was {was}, should be {res.significance_should_be}/5 {res.direction_should_be} ({res.confidence} confidence)</strong>
            </>)}
            {res.failure_cause && resultRow('Failure', `${res.failure_cause}${res.failure_detail ? ` - ${res.failure_detail}` : ''}`)}
            {a.audited_summary && resultRow('Summary audited', a.audited_summary)}
            {a.audited_analysis && resultRow('Analysis audited', <span style={{ whiteSpace: 'pre-wrap' }}>{a.audited_analysis}</span>)}
            {resultRow('Price action', `News-driven: ${res.news_driven}. ${res.price_explanation}`)}
            {res.group && res.group.size > 1 && resultRow(`Session - ${res.group.size} announcements audited together`,
              <ul style={{ margin: 0, paddingLeft: '18px' }}>
                {res.group.docs.map(d => (
                  <li key={d.n}>[{d.n}] {d.released} · {d.title}
                    <span style={s.muted}> - {d.role || 'role not given'}{d.audited ? '' : ' (context only)'}</span></li>
                ))}
              </ul>)}
            {resultRow('Reasoning', res.reasoning)}
            {resultRow('Missed', items(res.missed))}
            {resultRow('Misjudged', items(res.misjudged))}
            {res.analysis_issues && resultRow('Analysis issues', items(res.analysis_issues))}
            {resultRow('First news', `${res.first_news ? 'Yes' : 'No'} - ${res.first_news_reason}`)}
            {(res.expected_move_should_be || res.surprise_should_be) && resultRow('Expected move and surprise',
              `Move was ${a.audited_expected_move ? `${a.audited_expected_move}%` : 'not given'}, should be ${res.expected_move_should_be || '-'}`
              + ` (actual: ${res.actual_move || '-'}). Surprise was ${a.audited_surprise || 'not given'}, should be ${res.surprise_should_be || '-'}.`)}
            {resultRow(<>Suggested prompt change{res.prompt_change && <> · <code>{res.prompt_target || 'summary'}</code>
                <span style={{ ...s.link, marginLeft: '8px' }} onClick={() => navigate('/admin/prompts', { state: {
                  name: res.prompt_target || 'summary', suggestion: res.prompt_change, source: `audit ${a.audit_id} · ${r?.ticker} · ${a.model}` } })}>
                  open in AI Prompts</span></>}</>,
              res.prompt_change ? <pre style={s.pre}>{res.prompt_change}</pre> : 'none - the prompt worked')}
          </>
        )}
      </div>
    </div>
  );
}
